import { lazy, Suspense, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from './Footer';
import Header from './Header';
import DealsBanner from '../features/DealsBanner';
import ServiceBanner from '../features/ServiceBanner';
import PwaUpdateBanner from '../features/PwaUpdateBanner';
import { useI18n } from '../../hooks/useI18n';
import { useSettings } from '../../store/settingsStore';
import { useToastStore } from '../../store/toastStore';
import { useInstallModalStore } from '../../store/installModalStore';

const WhatsAppModal = lazy(() => import('../features/WhatsAppModal'));
const ChatWidget = lazy(() => import('../features/ChatWidget'));
const NotificationOptIn = lazy(() => import('../features/NotificationOptIn'));
const PromoPopup = lazy(() => import('../features/PromoPopup'));
const AppDownloadModal = lazy(() => import('../features/AppDownloadModal'));
const AccountPromptModal = lazy(() => import('../features/AccountPromptModal'));
const StorefrontTour = lazy(() => import('../features/StorefrontTour'));

const CUSTOMER_TOUR_SEEN_KEY = 'st_customer_tour_seen';

export default function Layout({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  const { t } = useI18n();
  const { customerTourEnabled } = useSettings();
  const addToast = useToastStore((state) => state.addToast);
  const installModalOpen = useInstallModalStore((state) => state.isOpen);
  const showStorefrontOverlays = pathname !== '/login' && pathname !== '/register';
  const isHome = pathname === '/';
  const isAdminRoute = pathname.startsWith('/admin');
  const [accountPromptOpen, setAccountPromptOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [tourClosed, setTourClosed] = useState(false);
  const [tourSeen, setTourSeen] = useState(() => typeof localStorage !== 'undefined' && localStorage.getItem(CUSTOMER_TOUR_SEEN_KEY) === '1');

  const isTourPreview = !isAdminRoute && new URLSearchParams(search).get('tour') === 'preview';
  const tourActive = showStorefrontOverlays && !isAdminRoute && !tourClosed && (isTourPreview || (customerTourEnabled && !tourSeen));
  const markTourSeen = () => {
    if (isTourPreview) return;
    try { localStorage.setItem(CUSTOMER_TOUR_SEEN_KEY, '1'); } catch { /* storage unavailable */ }
    setTourSeen(true);
  };

  useEffect(() => {
    const remind = () => addToast(t('auth_session_expired'), 'warning');
    window.addEventListener('authrequired', remind);
    return () => window.removeEventListener('authrequired', remind);
  }, [addToast, t]);

  useEffect(() => {
    let done = false;
    const update = () => {
      const header = document.querySelector('header') as HTMLElement | null;
      const banners = document.querySelectorAll<HTMLElement>('.deals-banner');
      const top =
        (header?.offsetHeight ?? 0) +
        Array.from(banners).reduce((sum, b) => sum + b.offsetHeight, 0);
      document.documentElement.style.setProperty('--snap-top', `${top}px`);
    };
    const unlock = () => {
      if (done) return;
      done = true;
      requestAnimationFrame(() => {
        update();
        document.documentElement.classList.remove('booting');
      });
    };
    update();
    window.addEventListener('resize', unlock);
    if (document.fonts?.ready) document.fonts.ready.then(unlock);
    else unlock();
    setTimeout(unlock, 900);
    return () => {
      window.removeEventListener('resize', unlock);
      document.documentElement.classList.remove('booting');
    };
  }, [pathname]);

  return (
    <div className="flex min-h-screen min-w-0 flex-col overflow-x-clip">
      <div className="sticky top-0 z-50">
        <Header />
        {!isAdminRoute && pathname === '/' ? (
          <>
            <ServiceBanner />
            <DealsBanner />
          </>
        ) : !isAdminRoute ? (
          <DealsBanner />
        ) : null}
      </div>
      <main data-dev-id="site.main" className="min-w-0 flex-1 pb-4">
        {children}
        {showStorefrontOverlays && !isAdminRoute && <><Suspense fallback={null}><ChatWidget /></Suspense><Suspense fallback={null}><WhatsAppModal /></Suspense><Suspense fallback={null}><AppDownloadModal /></Suspense></>}
      </main>
      {!isAdminRoute && (isHome ? null : <Footer />)}
      <PwaUpdateBanner />
      {showStorefrontOverlays && (
        <>
          <Suspense fallback={null}><AccountPromptModal onOpenChange={setAccountPromptOpen} blocked={installModalOpen || promoOpen} /></Suspense>
          <Suspense fallback={null}><NotificationOptIn accountPromptOpen={accountPromptOpen} promoOpen={promoOpen} /></Suspense>
          <Suspense fallback={null}><PromoPopup onOpenChange={setPromoOpen} blocked={accountPromptOpen} /></Suspense>
        </>
      )}
      {tourActive && (
        <Suspense fallback={null}>
          <StorefrontTour delayMs={isTourPreview ? 600 : 1400} onClose={() => { markTourSeen(); setTourClosed(true); }} onComplete={markTourSeen} />
        </Suspense>
      )}
    </div>
  );
}
